import React, { useEffect, useMemo, useState } from 'react';
import { ACTIVITY_TYPE, DATE_FORMATS } from 'src/shared/config/src';
import { fetchTimelineData, getCurrentChild, getTimelineCurrentPage, getTimelineData, getTimelineFilters, getTimelineLastPage, getTimelineLoading, getTimelinePaginatedLoading, isTimelineFilterApplied } from 'src/shared/redux-state/src';
import { ChildrenStoreData, TimelineData, TimelineStateType } from 'src/shared/types/src';
import { dateFormatter } from 'src/shared/utils/src';
import { useAppDispatch, useAppSelector } from '../../services/store';
import LocalImages from '../../static';
import {RegistryCard, DailyDiaryCard, ObservationCard, AccidentSheetCard, MedicineFormCard, ReportCard} from './components/timeline';
import { Spinner } from 'reactstrap';
import ChildrenLayout from 'src/app/layouts/ChildrenLayout';
import { TimelineActions } from 'src/shared/redux-state/src/lib/timeline/TimelineSlice';
import { isEqual, sortBy, debounce } from 'lodash';
import { TimelineLoader } from 'src/app/components';
import { URL_CONSTANTS } from 'src/app/config/appConstants';
import QuickFilters from './components/timeline/QuickFilters';
import { getParamsFromQuery, tryCatchWrapper } from 'src/app/utilities/helpers';

const ActivitiesScreen = () => {

  const dispatch = useAppDispatch();

  // const [filters, setFilters] = useState<TimelineStateType['filters']>({} as TimelineStateType['filters']);

  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
  const timelineLoading: boolean = useAppSelector(getTimelineLoading);
  const timelineData: TimelineData[] = useAppSelector(getTimelineData);
  const timelinePaginatedLoading: boolean = useAppSelector(getTimelinePaginatedLoading);
  const currentPage: number = useAppSelector(getTimelineCurrentPage);
  const lastPage: number = useAppSelector(getTimelineLastPage);
  const timelineFilterApplied = useAppSelector(isTimelineFilterApplied);
  const timelineFilters: TimelineStateType['filters'] = useAppSelector(getTimelineFilters);
  const [debouceLoading, setDebouceLoading] = useState(true);

  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);

  const runInitial = useMemo(
    () => debounce(async () => {
      setDebouceLoading(() => true);
      const searchParams = getParamsFromQuery()
      const fromDate: any = searchParams?.get(URL_CONSTANTS.FROM) ? searchParams.get(URL_CONSTANTS.FROM) : ''
      const toDate: any = searchParams?.get(URL_CONSTANTS.TO) ? searchParams.get(URL_CONSTANTS.TO) : ''
      const activityTypes: any = searchParams?.get(URL_CONSTANTS.ACTIVITY_TYPE) ? searchParams.get(URL_CONSTANTS.ACTIVITY_TYPE)?.split(',') : []
      let futureEvents: any = searchParams?.get(URL_CONSTANTS.FUTURE_EVENTS) ? searchParams.get(URL_CONSTANTS.FUTURE_EVENTS) : ''
      futureEvents = futureEvents === 'true'

      let isFilterApplied = false;
      if (activityTypes.length !== 0 || fromDate || toDate || futureEvents) {
        isFilterApplied = true;
      }
      if (isFirstTime && isFilterApplied && (fromDate !== timelineFilters.from || toDate !== timelineFilters.to || !isEqual(sortBy(activityTypes), sortBy(timelineFilters.activityTypes)) || futureEvents !== timelineFilters.showFutureEvents)) {
        await dispatch(TimelineActions.updateFilters({
          selectedTypes: activityTypes,
          from: dateFormatter(fromDate, DATE_FORMATS.YMD),
          to: dateFormatter(toDate, DATE_FORMATS.YMD),
          isFilterApplied,
          showFutureEvents: futureEvents
        }))
      }else{
        fetchTimelineList(1);
      }
      setIsFirstTime(false);
      setDebouceLoading(() => false);
    }, 300),
    [currentChild.childInstanceKey, timelineFilters]
  );

  useEffect(() => {
    runInitial();
    return () => runInitial.cancel();
  }, [runInitial]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100) {
        fetchNextPage();
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [timelineLoading, timelinePaginatedLoading, currentPage, lastPage]);

  const renderSwitch = (item: TimelineData) => {
    switch (item.activityType) {
      case ACTIVITY_TYPE.SIGN_IN:
      case ACTIVITY_TYPE.SIGN_OUT:
      case ACTIVITY_TYPE.DAILY_DIARY_HOLIDAY:
        return <RegistryCard timelineItem={item} />;
      case ACTIVITY_TYPE.DAILY_DIARY:
        return <DailyDiaryCard details={item.details} timestamp={item.timestamp} />
      case ACTIVITY_TYPE.OBSERVATION:
        // return <></>
        return <ObservationCard details={item.details} />
      case ACTIVITY_TYPE.ACCIDENT_SHEET:
        return <AccidentSheetCard timelineItem={item} />
      case ACTIVITY_TYPE.MEDICINE_FORM:
        return <MedicineFormCard timelineItem={item} />
      case ACTIVITY_TYPE.CUSTOM_SUMMATIVE_REPORT:
      case ACTIVITY_TYPE.CUSTOM_TRANSITION_REPORT:
      case ACTIVITY_TYPE.SUMMATIVE_REPORT:
      case ACTIVITY_TYPE.TRANSITION_REPORT:
      case ACTIVITY_TYPE.PROGRESS_CHECK_REPORT:
        return <ReportCard timelineItem={item} />
    }
  }

  const fetchNextPage = async () => {
    if (timelineLoading || timelinePaginatedLoading) return;
    if ((currentPage === lastPage) || timelinePaginatedLoading) return;
    await fetchTimelineList(currentPage + 1);
  };

  const fetchTimelineList = async (page: any) => {
    await tryCatchWrapper(
      async () => {
        if (!page) page = 1;
        let filterObj: any = {};
        if (timelineFilterApplied) {
    
          const { activityTypes, from, to, showFutureEvents } = timelineFilters;
          filterObj = {
            activityType: activityTypes.join(','),
            from,
            to,
            futureEvents: showFutureEvents
          };
    
          if (!activityTypes.length) delete filterObj['activityType'];
          if (!from) delete filterObj['from'];
          if (!to) delete filterObj['to'];
          if (!showFutureEvents) delete filterObj['futureEvents'];
        }
        await dispatch(fetchTimelineData({ child: currentChild, page, filterObj })).unwrap();
      })
  }

  const renderFooterLoader = () => {
    if (timelinePaginatedLoading && ((currentPage !== lastPage)))
      return (
        <div className='d-flex flex-column align-items-center justify-content-center mg-t-20'>
          <Spinner style={{ width: '20px', height: '20px' }} />
        </div>
      );
    return null;
  };

  const handleRenderItem = (item: TimelineData, index: number) => {

    let renderDateHeader = false;
    const currentActivity = timelineData[index];
    const currentActivityDate = dateFormatter(currentActivity.timestamp, DATE_FORMATS.YMD);
    if (index === 0) {
      renderDateHeader = true;
    } else {
      const previousActivity = timelineData[index - 1];
      const previousDate = dateFormatter(previousActivity.timestamp, DATE_FORMATS.YMD);
      if (previousDate !== currentActivityDate) {
        renderDateHeader = true;
      }
    }

    const currentDate = dateFormatter(new Date(), DATE_FORMATS.YMD);
    const yesterday = dateFormatter(new Date(new Date().setDate(new Date().getDate() - 1)), DATE_FORMATS.YMD);

    return (
      <>
        {
          renderDateHeader ?
            <div className='d-flex flex-wrap align-items-center mg-t-15'>
              <h6 className='fw-semibold text-black mg-b-0'>
                {currentActivityDate === currentDate ?
                  'Today' : (currentActivityDate === yesterday ? 'Yesterday' : dateFormatter(currentActivityDate, DATE_FORMATS.DAY_OF_WEEK))}
              </h6>
              <p className='mg-l-5 mg-b-0 fs-12 text-black'>{dateFormatter(item.timestamp, DATE_FORMATS.SHORT_DATE)}</p>
            </div> : null
        }
        <>
          {
            renderSwitch(item)
          }
        </>

      </>
    )
  }

  const renderTimelineList = () => {
    const showLerningJourneyIcon = timelineFilters?.activityTypes?.length === 1
      && timelineFilters?.activityTypes.includes(ACTIVITY_TYPE.OBSERVATION);
    return (
      <ChildrenLayout fixedFooter={true} showLearningJourneyIcon={showLerningJourneyIcon}>
        <QuickFilters timelineFilters={timelineFilters} />
        {
          timelineLoading || debouceLoading ? <TimelineLoader /> :
            <>
              {
                timelineData && timelineData.length > 0 ?
                  timelineData.map((item, index) => handleRenderItem(item, index))
                  : <NoActivities />
              }
            </>
        }
        {renderFooterLoader()}
      </ChildrenLayout>
    )
  }

  return (
    renderTimelineList()
  );

}

const NoActivities = () => {
  return (
    <>
      <div className='d-flex flex-column align-items-center justify-content-center no-activities'>
        <img src={LocalImages.noActivities} alt="no Activities" />
        <p className='text-center fs-12 text-black mg-b-0 mg-t-10'>No activities found!</p>
      </div>
    </>
  );
};

export default ActivitiesScreen;